import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig } from './TrafficLandingTemplate';
import { Activity, BarChart3, Globe } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'pt/trafego-site-atacado',
    titleTag: 'Comprar Tráfego por Atacado | Traffic Creator',
    metaDescription: 'Compre tráfego de site por atacado a preços de revenda. Tráfego web escalável para agências e revendedores — visitantes reais, verificado por GA4. Descontos por volume de até 40%. Comece grátis.',
    keywords: 'Comprar Tráfego por Atacado, Tráfego Web por Atacado, Comprar Tráfego Web de Atacado, Tráfego Web em Bloco, Tráfego para Revendedores, Tráfego Barato para Sites',
    primaryKeyword: 'Comprar Tráfego por Atacado',
    secondaryKeyword: 'Tráfego Web por Atacado',
    reviewCount: '590',
    ratingValue: '4.9',

    h1: 'A Melhor Forma de Comprar Tráfego em Massa Online',
    subheadline: 'Simplifique sua estratégia com tráfego por atacado de alta qualidade. Escale suas campanhas com milhões de visitantes a preços competitivos — verificado no GA4.',

    problemH2: 'Dificuldade em Escalar seu Tráfego com Baixo Custo?',
    problemText: 'Gerir grandes campanhas exige volumes massivos sem comprometer suas margens de lucro. Anúncios tradicionais são caros e lentos. Nossa solução de tráfego em massa oferece visitantes constantes a uma fração do preço de mercado.',
    solutionPoints: [
        'Descontos por volume de até 40% — escale sua agência com lucro',
        'IPs residenciais reais com navegação natural, 100% visível no GA4',
        'Suporte dedicado para agências trabalhando com múltiplos domínios',
        'Tráfego segmentado em 190+ países para atingir qualquer mercado global'
    ],

    featuresH2: 'Por que Usar Nosso Tráfego em Massa?',
    features: [
        {
            icon: <Activity />,
            title: 'Escalabilidade Econômica',
            description: 'Aumente o volume de visitas sem estourar o orçamento. Nossos pacotes de atacado oferecem até 40% de desconto, sendo perfeitos para revendedores e agências de marketing.'
        },
        {
            icon: <BarChart3 />,
            title: 'Rastreamento Real no Analytics',
            description: 'Cada visita conta. Todo o tráfego em massa aparece nativamente no seu painel do Google Analytics 4 com sessões reais e dados de engajamento.'
        },
        {
            icon: <Globe />,
            title: 'Segmentação Global Precisa',
            description: 'Direcione tráfego de países e regiões específicas com precisão. Se você precisa de visitas no Brasil, EUA ou Europa, nossa rede entrega em 190+ países.'
        }
    ],

    testimonialsH2: 'O que Nossos Clientes Dizem sobre o Tráfego por Atacado',
    testimonials: [
        {
            name: 'David M.',
            role: 'Dono de Agência Digital',
            result: '+300% ROI do Cliente',
            text: 'Mudamos para os pacotes de tráfego em massa e reduzimos nosso custo por visita em 40%. Agora revendemos tráfego para 50+ clientes com margens excelentes.',
            seed: 'davidm_pt'
        },
        {
            name: 'Lisa T.',
            role: 'Manager de Marketing de Afiliados',
            result: '5M Visitantes/Mês',
            text: 'Gerenciar dezenas de sites de nicho exige volume bruto. Esta solução nos entrega 5 milhões de visitas mensais com total confiança no GA4.',
            seed: 'lisat_pt'
        },
        {
            name: 'Carlos R.',
            role: 'Revendedor de SEO',
            result: '-40% Custo por Visita',
            text: 'O desconto por massa reduziu drasticamente nosso custo unitário. Como revendedor, essa estrutura permite oferecer taxas competitivas aos nossos clientes.',
            seed: 'carlosr_pt'
        }
    ],

    ctaH2: 'Pronto para Comprar Tráfego em Massa?',

    faqs: [
        {
            question: 'O que é tráfego em massa?',
            answer: 'É a compra de grandes volumes de visitantes web com descontos de atacado. Ideal para quem precisa de escala sem pagar o preço cheio por visita.'
        },
        {
            question: 'Como funcionam os descontos de atacado?',
            answer: 'Quanto maior o pacote de créditos adquirido, menor o valor por visitante. Os descontos chegam a 40% nos maiores pacotes.'
        },
        {
            question: 'É seguro comprar tráfego em grandes quantidades?',
            answer: 'Sim, desde que use IPs residenciais reais como nós. Isso garante que o tráfego seja visto como 100% natural pelo Google e sistemas de análise.'
        },
        {
            question: 'O tráfego aparecerá no Google Analytics?',
            answer: 'Com certeza. Todo o tráfego é visível em tempo real no GA4. Você poderá acompanhar sessões, países de origem e tempo de permanência.'
        },
        {
            question: 'Quanto tempo leva para começar a entrega?',
            answer: 'Após a configuração, o sistema inicia o envio em menos de 5 minutos. Volumes massivos são distribuídos gradualmente para manter um perfil natural.'
        }
    ],

    relatedLinks: [
        { label: 'Comprar tráfego web', to: '/pt/comprar-trafego-site' },
        { label: 'Tráfego orgânico', to: '/pt/comprar-trafego-organico' },
        { label: 'Booster de SEO', to: '/pt/seo-traffic-booster' },
    ],

    productName: 'Comprar Tráfego em Massa - Preços de Atacado para Agências',
    productDescription: 'Precisa de volume massivo com preço de atacado? Compre tráfego em massa através da nossa rede residencial. Escala real para agências. Comece hoje.',

    translations: {
        navFeatures: 'Características',
        navPricing: 'Preços Atacado',
        navFaq: 'FAQ',
        navLogin: 'Login',
        heroBadge: 'Tráfego Premium em Volume',
        heroCtaPrimary: 'Teste Grátis',
        heroCtaSecondary: 'Ver Preços',
        heroTrust: 'Mais de 10.000 agências confiam em nós',
        heroMicrocopy: 'Sem cartão de crédito. 100% seguro e verificado pelo Analytics.',
        heroLiveLabel: 'Live',
        heroTrafficIncrease: 'Aumento de Tráfego',
        heroActiveLabel: 'Ativo',
        heroVisitorsServed: 'Visitantes Entregues',
        socialProofLabel: 'Parceiros de confiança:',
        problemWithout: 'Sem nós',
        problemWith: 'Com nós',
        problemBullets: [
            'Custos publicitários insustentáveis',
            'Falta de visibilidade para projetos grandes',
            'Dificuldade em escalar vendas rapidamente',
            'Altas taxas de rejeição prejudiciais',
        ],
        statsCampaigns: 'Campanhas Ativas',
        statsCountries: 'Países',
        statsUptime: 'Uptime',
        statsSupport: 'Suporte',
        featuresSubtitle: 'Tudo o que você precisa para um crescimento massivo e lucrativo.',
        howItWorksH2: '3 Passos para Escalar',
        howItWorksSteps: [
            { title: 'Configurar Pack', desc: 'Escolha os países, dispositivos e o volume total necessário.' },
            { title: 'Lançar Rede', desc: 'Relaxe enquanto nossas IPs residenciais ativam as visitas no seu site.' },
            { title: 'Analisar Dados', desc: 'Acompanhe seus milhões de visitas em tempo real no dashboard do GA4.' },
        ],
        pricingH2: 'Modelo de Preços Atacado',
        pricingSubtitle: 'Pagamento único. Sem assinaturas. Os créditos nunca expiram.',
        pricingMostPopular: 'Escolha das Agências',
        pricingVisitors: 'Visitantes',
        pricingGetStarted: 'Começar Agora',
        pricingGuarantee: 'Garantia de reembolso de 30 dias',
        pricingDelivery: 'Entrega imediata (~5 min)',
        pricingCountries: 'Mais de 190 Países',
        faqH2: 'Perguntas Frequentes',
        ctaSubtitle: 'Junte-se a 10.000 clientes profissionais e reduza seus custos hoje mesmo.',
        ctaButtonPrimary: 'Provar Grátis',
        ctaButtonSecondary: 'Ver Detalhes do Atacado',
        ctaMicrocopy: 'Sem compromissos. 100% seguro.',
        footerDescription: 'Plataforma líder para compra de tráfego web massivo e autenticado.',
        footerRelatedServices: 'Serviços Relacionados',
        footerCompany: 'Empresa',
        footerAbout: 'Sobre Nós',
        footerBlog: 'Blog',
        footerHelpCenter: 'Ajuda',
        footerPrivacy: 'Privacidade',
        footerImpressum: 'Informações Legais',
        footerStatus: 'Sistemas Operacionais',
        footerBuyTraffic: 'Comprar Tráfego Web',
    },
};

const PtBulkWebsiteTraffic: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default PtBulkWebsiteTraffic;
